import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DEFAULT_STALE_TIME = 5 * 60 * 1000;
const AppDataContext = createContext(null);

function getInitialEntry() {
  return {
    data: null,
    loading: false,
    error: '',
    fetchedAt: 0,
  };
}

export function AppDataProvider({ children }) {
  const [resources, setResources] = useState({});
  const resourcesRef = useRef(resources);
  const inflightRef = useRef(new Map());

  useEffect(() => {
    resourcesRef.current = resources;
  }, [resources]);

  const setResourceEntry = useCallback((key, updater) => {
    setResources((prev) => {
      const current = prev[key] || getInitialEntry();
      return {
        ...prev,
        [key]: typeof updater === 'function' ? updater(current) : updater,
      };
    });
  }, []);

  const fetchResource = useCallback(
    async (key, loader, options = {}) => {
      const staleTime = options.staleTime ?? DEFAULT_STALE_TIME;
      const force = options.force === true;
      const current = resourcesRef.current[key];
      const now = Date.now();

      if (
        !force &&
        current?.fetchedAt &&
        now - current.fetchedAt < staleTime
      ) {
        return current.data;
      }

      if (!force && inflightRef.current.has(key)) {
        return inflightRef.current.get(key);
      }

      setResourceEntry(key, (entry) => ({
        ...entry,
        loading: true,
        error: '',
      }));

      const request = Promise.resolve()
        .then(loader)
        .then((data) => {
          setResourceEntry(key, {
            data,
            loading: false,
            error: '',
            fetchedAt: Date.now(),
          });
          return data;
        })
        .catch((error) => {
          setResourceEntry(key, (entry) => ({
            ...entry,
            loading: false,
            error: error.message || 'Something went wrong.',
          }));
          throw error;
        })
        .finally(() => {
          inflightRef.current.delete(key);
        });

      inflightRef.current.set(key, request);
      return request;
    },
    [setResourceEntry],
  );

  const refreshResource = useCallback(
    (key, loader, options = {}) =>
      fetchResource(key, loader, { ...options, force: true }),
    [fetchResource],
  );

  const updateResource = useCallback(
    (key, updater) => {
      setResourceEntry(key, (entry) => ({
        ...entry,
        data: updater(entry.data),
        fetchedAt: Date.now(),
      }));
    },
    [setResourceEntry],
  );

  const invalidateResource = useCallback((key) => {
    setResources((prev) => {
      if (!prev[key]) {
        return prev;
      }

      return {
        ...prev,
        [key]: {
          ...prev[key],
          fetchedAt: 0,
        },
      };
    });
  }, []);

  const invalidateResourcesByPrefix = useCallback((prefix) => {
    setResources((prev) => {
      let changed = false;
      const next = {};

      for (const [key, entry] of Object.entries(prev)) {
        if (key.startsWith(prefix)) {
          changed = true;
          next[key] = {
            ...entry,
            fetchedAt: 0,
          };
        } else {
          next[key] = entry;
        }
      }

      return changed ? next : prev;
    });
  }, []);

  const clearResources = useCallback(() => {
    inflightRef.current.clear();
    setResources({});
  }, []);

  const value = useMemo(
    () => ({
      resources,
      fetchResource,
      refreshResource,
      updateResource,
      invalidateResource,
      invalidateResourcesByPrefix,
      clearResources,
    }),
    [
      resources,
      fetchResource,
      refreshResource,
      updateResource,
      invalidateResource,
      invalidateResourcesByPrefix,
      clearResources,
    ],
  );

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppDataContext);

  if (!context) {
    throw new Error('useAppData must be used within AppDataProvider');
  }

  return context;
}

export function useAppResource(key, loader, deps = [], options = {}) {
  const { resources, fetchResource, refreshResource, invalidateResource } =
    useAppData();
  const entry = resources[key] || getInitialEntry();

  useEffect(() => {
    let active = true;

    fetchResource(key, loader, options).catch(() => {
      if (!active) {
        return;
      }
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, fetchResource, ...deps]);

  const refresh = useCallback(
    (refreshOptions = {}) =>
      refreshResource(key, loader, { ...options, ...refreshOptions }),
    [key, loader, options, refreshResource],
  );

  const invalidate = useCallback(
    () => invalidateResource(key),
    [key, invalidateResource],
  );

  return {
    data: entry.data ?? options.initialData,
    loading: entry.loading && !entry.data,
    refreshing: entry.loading && Boolean(entry.data),
    error: entry.error,
    fetchedAt: entry.fetchedAt,
    refresh,
    invalidate,
  };
}
